import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d6kpbu46fojmejgaio00', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="bg-slate-900 py-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-start mb-16">
          <div className="mb-8 md:mb-0">
            <h2 className="text-8xl font-extrabold text-white mb-4" style={{ letterSpacing: '0.05em' }}>
              VALIDE d.o.o.
            </h2>
            <div className="flex items-center">
              <div className="w-32 h-0.5 bg-teal-500 mr-4"></div>
              <span className="text-base text-gray-400">iOS Development Experts</span>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-sm text-gray-400 mb-4">© 2025 VALIDE d.o.o.</p>
            <div className="flex space-x-5">
              <a href="#" className="text-teal-500 hover:text-white transition-colors duration-200">
                <i className="ri-linkedin-fill text-2xl"></i>
              </a>
              <a href="#" className="text-teal-500 hover:text-white transition-colors duration-200">
                <i className="ri-twitter-fill text-2xl"></i>
              </a>
              <a href="#" className="text-teal-500 hover:text-white transition-colors duration-200">
                <i className="ri-github-fill text-2xl"></i>
              </a>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-16 mb-16">
          <div>
            <h3 className="text-3xl font-bold text-white mb-8">Get in Touch</h3>
            <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500 transition-colors"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500 transition-colors"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500 transition-colors"
                  placeholder="How can we help?"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500 transition-colors resize-none"
                  placeholder="Your message (max 500 characters)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-400 text-sm">
                  Message sent successfully! We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">
                  Failed to send message. Please try again.
                </div>
              )}
            </form>
          </div>

          <div>
            <h3 className="text-3xl font-bold text-white mb-8">Contact Information</h3>
            <div className="space-y-8">
              <div>
                <div className="flex items-center mb-3">
                  <i className="ri-map-pin-line text-2xl text-teal-500 mr-3"></i>
                  <span className="text-xs uppercase text-gray-400 tracking-wider">Address</span>
                </div>
                <p className="text-base text-white leading-relaxed ml-11">
                  Kladezna ulica 20<br />
                  Ljubljana, 1000<br />
                  Slovenia
                </p>
              </div>

              <div>
                <div className="flex items-center mb-3">
                  <i className="ri-mail-line text-2xl text-teal-500 mr-3"></i>
                  <span className="text-xs uppercase text-gray-400 tracking-wider">Email</span>
                </div>
                <a
                  href="mailto:help@validedoo.com"
                  className="text-base text-white hover:text-teal-400 transition-colors ml-11"
                >
                  help@validedoo.com
                </a>
              </div>

              <div>
                <div className="flex items-center mb-3">
                  <i className="ri-phone-line text-2xl text-teal-500 mr-3"></i>
                  <span className="text-xs uppercase text-gray-400 tracking-wider">Phone</span>
                </div>
                <a
                  href="tel:+12315946820"
                  className="text-base text-white hover:text-teal-400 transition-colors ml-11"
                >
                  +1 2315946820
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}